import React, { useState, useEffect, useRef } from 'react';
import { Send, Zap, User, Bot, Loader2, ArrowRight, Coins, X, Paperclip, Image as ImageIcon, MessageSquare, Trash2, Scissors, Copy, ArrowUp, ArrowDown, Square, Award, Brain } from 'lucide-react';
import { type ChatMessage, uploadChatImage, deleteMessage, deleteUserMessage, clearVersionMessages, evaluateVersion, getVersionMindmap, type AuditResponse, type MindmapResponse } from '../../../../lib/apis_vibe';
import { getStudentCourses, type PublicCourseSummaryOut, type PublicCourseStageSummaryOut } from '../../../../lib/apis_publish';
import TokenWarningModal from '../../../../components/common/TokenWarningModal';
import { ReasoningMarquee } from '../../../../components/common/ReasoningMarquee';
import { StreamingCodeBlock } from '../../../../components/common/StreamingCodeBlock';
import { mergeHtmlFences } from '../../../../lib/htmlChunkAssembler';
import AuditModal from './AuditModal';
import MindmapModal from './MindmapModal';
import LearningObjectivesModal from './LearningObjectivesModal';

interface ChatPanelProps {
  messages: ChatMessage[];
  onSendMessage: (text: string, images?: string[], model?: 'gemini' | 'nanobanana', help?: string) => void;
  onApplyCode: (code: string) => void;
  isAiThinking: boolean;
  tokens: number;
  onDeleteMessage: (messageId: number) => void;
  onClearMessages: () => void;
  versionId?: number;
  userRole?: string;
  currentReasoning?: string;
  onStopGeneration: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages, onSendMessage, onApplyCode, isAiThinking, onDeleteMessage, onClearMessages, versionId, userRole, currentReasoning = '', onStopGeneration
}) => {
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]); 
  const [isUploading, setIsUploading] = useState(false);
  const [selectedModel, setSelectedModel] = useState<'gemini' | 'nanobanana'>('gemini');
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const [showTokenWarning, setShowTokenWarning] = useState(false);
  const [warningTokenCount, setWarningTokenCount] = useState(0);
  
  // 老师端和学生端统一使用 vibe_coding 系统提示词
  const [selectedHelp] = useState<'vibe_marketing' | 'vibe_coding'>('vibe_coding');

  // --- Vibe Audit 评分与思维链 ---
  const [stages, setStages] = useState<PublicCourseStageSummaryOut[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isMindmapLoading, setIsMindmapLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<AuditResponse | null>(null);
  const [mindmapResult, setMindmapResult] = useState<MindmapResponse | null>(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showMindmapModal, setShowMindmapModal] = useState(false);
  const [showEvaluateConfirmModal, setShowEvaluateConfirmModal] = useState(false);
  const [showMindmapConfirmModal, setShowMindmapConfirmModal] = useState(false);
  const [showLearningObjectivesModal, setShowLearningObjectivesModal] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 获取 course id=5 的 stages/lessons（用于评分按钮的 Stage/Lesson 下拉）
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getStudentCourses();
        const courses = (res as any).data || res;
        const vibeCourse = (courses as PublicCourseSummaryOut[]).find(c => c.id === 5);
        if (!vibeCourse) return;
        const sortedStages = [...vibeCourse.stages].sort((a, b) => a.sort_order - b.sort_order);
        if (!mounted) return;
        setStages(sortedStages);
        if (sortedStages.length > 0) {
          setSelectedStageId(sortedStages[0].id);
          const firstLesson = [...sortedStages[0].lessons].sort((a, b) => a.sort_order - b.sort_order)[0];
          setSelectedLessonId(firstLesson?.id ?? null);
        }
      } catch (e) {
        console.warn('Failed to load vibe course stages', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Stage 切换：重置 Lesson 并自动选第一个
  const handleStageChange = (stageId: number) => {
    setSelectedStageId(stageId);
    const stage = stages.find(s => s.id === stageId);
    const firstLesson = stage
      ? [...stage.lessons].sort((a, b) => a.sort_order - b.sort_order)[0]
      : null;
    setSelectedLessonId(firstLesson?.id ?? null);
  };

  const handleLessonChange = (lessonId: number) => {
    setSelectedLessonId(lessonId);
  };

  // 评分按钮：打开确认框
  const handleEvaluate = () => {
    if (!versionId || !selectedLessonId) {
      alert('请先选择 Stage 和 Lesson，并确保已打开文件版本');
      return;
    }
    setShowEvaluateConfirmModal(true);
  };

  // 确认后执行评分
  const executeEvaluate = async () => {
    setShowEvaluateConfirmModal(false);
    setIsEvaluating(true);
    setShowAuditModal(true);
    setAuditResult(null);
    try {
      const res = await evaluateVersion(selectedLessonId!, versionId!, { model: 'gemini_high' });
      const data = (res as any).data || res;
      setAuditResult(data as AuditResponse);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '未知错误';
      alert(`评分失败：${detail}`);
      setShowAuditModal(false);
    } finally {
      setIsEvaluating(false);
    }
  };

  // 思维链按钮：打开确认框
  const handleMindmap = () => {
    if (!versionId) {
      alert('请先确保已打开文件版本');
      return;
    }
    setShowMindmapConfirmModal(true);
  };

  // 确认后执行思维链生成
  const executeMindmap = async () => {
    setShowMindmapConfirmModal(false);
    setIsMindmapLoading(true);
    setShowMindmapModal(true);
    setMindmapResult(null);
    try {
      const res = await getVersionMindmap(versionId!, {
        model: 'gemini_high',
        lesson_id: selectedLessonId ?? undefined
      });
      const data = (res as any).data || res;
      setMindmapResult(data as MindmapResponse);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || '未知错误';
      alert(`思维链生成失败：${detail}`);
      setShowMindmapModal(false);
    } finally {
      setIsMindmapLoading(false);
    }
  };

  // 学习目标查看按钮：直接打开弹窗（无需确认框，不消耗 Token）
  const handleShowLearningObjectives = () => {
    if (!selectedLessonId) {
      alert('请先选择 Stage 和 Lesson');
      return;
    }
    setShowLearningObjectivesModal(true);
  };

  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\s<>"{}|\\^`\[\]]*)?)/gi;

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    if (input.trim() === '') {
      const matches = value.match(urlRegex);
      
      if (matches && matches.length > 0) {
        const newUrls: string[] = [];
        let cleanedValue = value;
        
        matches.forEach(url => {
          if (!selectedFiles.includes(url) && !newUrls.includes(url)) {
            newUrls.push(url);
            cleanedValue = cleanedValue.replace(url, '').trim();
          }
        });
        
        if (newUrls.length > 0) {
          setSelectedFiles(prev => [...prev, ...newUrls]);
          setInput(cleanedValue.replace(/\s+/g, ' ').trim());
          return;
        }
      }
    }
    
    setInput(value);
  };

  // --- Effects ---
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, isAiThinking, currentReasoning]);

  useEffect(() => {
    if (messages.length > 0 && !isAiThinking) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.input_tokens && lastMessage.input_tokens >= 190000) {
        setWarningTokenCount(lastMessage.input_tokens);
        setShowTokenWarning(true);
      }
    }
  }, [messages, isAiThinking]);

  // 自动调整输入框高度
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  // --- Handlers ---

  // 1. 发送前检查 (拦截生图请求)
  const handleSendCheck = () => {
    if ((!input.trim() && selectedFiles.length === 0) || isUploading || isAiThinking) return;
    
    // 如果是生图模型，弹出确认框
    if (selectedModel === 'nanobanana') {
      setShowConfirmModal(true);
    } else {
      executeSend('gemini');
    }
  };

  // 2. 执行发送
  const executeSend = (model: 'gemini' | 'nanobanana') => {
    onSendMessage(input, selectedFiles, model, selectedHelp);
    
    if (model !== selectedModel) {
      setSelectedModel(model);
    }

    setInput('');
    setSelectedFiles([]);
    setShowConfirmModal(false);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const res = await uploadChatImage(file);
      const url = (res as any).data?.url || (res as any).url || (res as any).oss_url; 
      
      if (url) {
        setSelectedFiles(prev => [...prev, url]);
      } else {
        alert("上传失败：服务器未返回图片地址");
      }
    } catch (err) {
      alert("上传失败");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = (idx: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteMessage = async (messageId: number) => {
    if (!confirm('确定要删除这条对话吗？')) return;
    
    try {
      await deleteMessage(messageId);
      onDeleteMessage(messageId);
    } catch (err) {
      console.error('Delete message failed:', err);
      alert('删除失败，请稍后重试');
    }
  };

  const handleDeleteUserMessage = async (messageId: number) => {
    if (!confirm('确定要删除这条用户消息吗？')) return;
    
    try {
      await deleteUserMessage(messageId);
      onDeleteMessage(messageId);
    } catch (err) {
      console.error('Delete user message failed:', err);
      alert('删除失败，请稍后重试');
    }
  };

  const handleResendMessage = (aiMessageId: number) => {
    // 找到当前AI消息的索引
    const aiMsgIndex = messages.findIndex(msg => msg.id === aiMessageId);
    if (aiMsgIndex === -1) return;
    
    // 向前遍历找到最近的用户消息
    for (let i = aiMsgIndex - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'user') {
        // 将用户消息内容放入输入框
        setInput(msg.content);
        break;
      }
    }
  };

  const handleClearMessages = async () => {
    setShowClearConfirmModal(true);
  };

  const executeClearMessages = async () => {
    if (!versionId) {
      alert('无法清空：未找到版本ID');
      setShowClearConfirmModal(false);
      return;
    }
    
    try {
      await clearVersionMessages(versionId);
      onClearMessages();
      setShowClearConfirmModal(false);
    } catch (err) {
      console.error('Clear messages failed:', err);
      alert('清空失败，请稍后重试');
    }
  };

  const handleCutout = () => {
    if (selectedFiles.length === 0) {
      alert('请先上传图片');
      return;
    }
    
    setSelectedModel('nanobanana');
    
    const cutoutContent = `抠图：${input}`;
    
    onSendMessage(cutoutContent, selectedFiles, 'nanobanana', selectedHelp);
    setInput('');
    setSelectedFiles([]);
    setShowConfirmModal(false);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleCopyMessage = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      alert('已复制到剪贴板');
    } catch (err) {
      console.error('Copy failed:', err);
      alert('复制失败，请稍后重试');
    }
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setIsUploading(true);
          try {
            const res = await uploadChatImage(file);
            const url = (res as any).data?.url || (res as any).url || (res as any).oss_url;
            
            if (url) {
              setSelectedFiles(prev => [...prev, url]);
            } else {
              alert("上传失败：服务器未返回图片地址");
            }
          } catch (err) {
            console.error('Paste upload failed', err);
            alert('粘贴图片上传失败');
          } finally {
            setIsUploading(false);
          }
        }
      }
    }
  };

  // --- 确认框辅助变量：当前选中的 lesson 标签 ---
  const currentLesson = stages
    .flatMap(s => s.lessons)
    .find(l => l.id === selectedLessonId);
  const lessonLabel = currentLesson
    ? `第${currentLesson.sort_order}课 ${currentLesson.title}`
    : '';

  // --- Render Helpers ---

  const renderCodeCard = (code: string, lang: string, key: string | number, isTruncated: boolean = false) => (
    <StreamingCodeBlock
      key={key}
      code={code}
      language={lang}
      isTruncated={isTruncated}
      theme="dark"
      onApplyCode={onApplyCode}
    />
  );

  const renderMessageContent = (content: string) => {
    // 合并多轮 HTML 代码围栏，确保渲染为单个 <pre><code> 块
    const mergedContent = mergeHtmlFences(content);
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    // 匹配完整代码块
    const completeBlockRegex = /```(\w+)?\s*([\s\S]*?)```/g;
    let match;

    while ((match = completeBlockRegex.exec(mergedContent)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`} className="whitespace-pre-wrap break-words">
            {mergedContent.substring(lastIndex, match.index)}
          </span>
        );
      }
      const lang = match[1] || 'text';
      const code = match[2].trim();
      parts.push(renderCodeCard(code, lang, `code-${match.index}`));
      lastIndex = completeBlockRegex.lastIndex;
    }

    // 处理剩余文本（检测截断）
    const remaining = mergedContent.substring(lastIndex);
    const unclosedMarkdownMatch = /```(\w+)?\s*([\s\S]*)$/.exec(remaining);
    const rawHtmlStartIdx = remaining.indexOf('<!DOCTYPE html>');
    const isRawHtmlTruncated = rawHtmlStartIdx !== -1 && !remaining.includes('</html>');

    if (unclosedMarkdownMatch) {
      const preText = remaining.substring(0, unclosedMarkdownMatch.index);
      if (preText) parts.push(<span key="text-tail-pre" className="whitespace-pre-wrap break-words">{preText}</span>);

      const lang = unclosedMarkdownMatch[1] || 'html';
      const code = unclosedMarkdownMatch[2];
      parts.push(renderCodeCard(code, lang, 'code-truncated-md', true));
    }
    else if (isRawHtmlTruncated) {
      const preText = remaining.substring(0, rawHtmlStartIdx);
      if (preText) parts.push(<span key="text-tail-pre" className="whitespace-pre-wrap break-words">{preText}</span>);

      const code = remaining.substring(rawHtmlStartIdx);
      parts.push(renderCodeCard(code, 'html', 'code-truncated-raw', true));
    }
    else if (remaining) {
      parts.push(
        <span key="text-tail" className="whitespace-pre-wrap break-words">
          {remaining}
        </span>
      );
    }

    return parts.length > 0 ? parts : <div className="whitespace-pre-wrap break-words">{content}</div>;
  };

  return (
    <div className="flex flex-col h-full bg-[#0c0c0e] border-r border-white/10 w-[400px] shrink-0 relative">
      
      {/* 1. Header */}
      <div className="h-20 bg-gradient-to-b from-purple-900/20 to-transparent flex flex-col justify-center px-6 border-b border-white/5 relative overflow-hidden shrink-0">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>

        {/* 第一行：缩小闪电图标 + Vibe AI 文字，同行 */}
        <div className="relative flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-purple-600 to-blue-600 p-[1.5px] shadow-[0_0_15px_rgba(168,85,247,0.3)] animate-pulse">
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
              <Zap className="text-white w-4 h-4" />
            </div>
          </div>
          <h2 className="font-bold text-white text-lg leading-none">Vibe AI</h2>
        </div>

        {/* 第二行：下拉列表 + emoji按钮 + 评分/思维链按钮，整体左移 */}
        <div className="relative flex items-center gap-2 mt-2">
          {/* Stage 下拉 */}
          <select
            value={selectedStageId ?? ''}
            onChange={(e) => handleStageChange(Number(e.target.value))}
            className="text-xs bg-black/50 text-white border border-white/10 rounded-full px-2 py-1 outline-none focus:border-purple-500/50 max-w-[120px]"
            title="选择 Stage"
          >
            {stages.map(s => (
              <option key={s.id} value={s.id} className="bg-[#1a1a1a]">
                Stage {s.sort_order}
              </option>
            ))}
          </select>
          {/* Lesson 下拉（级联） */}
          <select
            value={selectedLessonId ?? ''}
            onChange={(e) => handleLessonChange(Number(e.target.value))}
            disabled={!selectedStageId}
            className="text-xs bg-black/50 text-white border border-white/10 rounded-full px-2 py-1 outline-none focus:border-purple-500/50 max-w-[140px] disabled:opacity-40"
            title="选择 Lesson"
          >
            {(() => {
              const stage = stages.find(s => s.id === selectedStageId);
              if (!stage) return null;
              return [...stage.lessons]
                .sort((a, b) => a.sort_order - b.sort_order)
                .map(l => (
                  <option key={l.id} value={l.id} className="bg-[#1a1a1a]">
                    第{l.sort_order}课 {l.title}
                  </option>
                ));
            })()}
          </select>
          {/* 🎯 学习目标 emoji 按钮 */}
          <button
            onClick={handleShowLearningObjectives}
            disabled={!selectedLessonId}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-pink-500/30 bg-pink-500/10 text-base hover:bg-pink-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="查看本课学习目标"
          >
            🎯
          </button>
          {/* 评分按钮 */}
          <button
            onClick={handleEvaluate}
            disabled={!selectedLessonId || !versionId || isEvaluating}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="AI 代码评测"
          >
            {isEvaluating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
          </button>
          {/* 思维链按钮 */}
          <button
            onClick={handleMindmap}
            disabled={!versionId || isMindmapLoading}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 hover:text-cyan-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="思维决策链"
          >
            {isMindmapLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* 2. Messages List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 text-sm mt-10">
            👋 嗨！我是你的编程助手。<br/>想改什么？直接告诉我！
          </div>
        )}
        
        {messages.map((msg) => {
          const displayImages: { url: string, isAi: boolean }[] = [];
          const seenUrls = new Set<string>();

          // 图片去重合并逻辑
          if (Array.isArray(msg.image_urls)) {
            msg.image_urls.forEach(u => {
              if (u && !seenUrls.has(u)) {
                displayImages.push({ url: u, isAi: false });
                seenUrls.add(u);
              }
            });
          }
          if (msg.files) {
            msg.files.forEach(f => {
              if (f.oss_url && !seenUrls.has(f.oss_url)) {
                if (f.file_type === 'image_input' || f.file_type === 'image_output') {
                  displayImages.push({ 
                    url: f.oss_url, 
                    isAi: f.file_type === 'image_output' 
                  });
                  seenUrls.add(f.oss_url);
                }
              }
            });
          }

          return (
            <div key={msg.id || Math.random()} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-lg ${msg.role === 'assistant' ? 'bg-purple-600' : 'bg-blue-600'}`}>
                {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
              </div>

              <div className={`max-w-[90%] rounded-2xl p-3.5 text-sm leading-relaxed relative group ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-sm shadow-blue-900/20' : 'bg-[#18181b] text-gray-200 border border-white/10 rounded-tl-sm'}`}>
                
                {/* 图片展示 */}
                {displayImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {displayImages.map((img, idx) => (
                      <div key={idx} className="relative group/img overflow-hidden rounded border border-white/10">
                        <img 
                          src={img.url} 
                          alt="attachment" 
                          className={`${img.isAi ? 'w-full h-auto max-h-60' : 'w-24 h-24'} object-cover`}
                        />
                        <a href={img.url} target="_blank" rel="noreferrer" className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"/>
                      </div>
                    ))}
                  </div>
                )}

                {renderMessageContent(msg.content)}

                {/* ⚠️ 内容不完整提示（meta.partial === true） */}
                {msg.partial === true && (
                  <div className="mt-2 px-3 py-2 bg-orange-900/20 border border-orange-700/30 rounded-lg text-xs text-orange-400 flex items-center gap-2">
                    <span>⚠️</span>
                    <span>内容可能不完整{msg.partialReason ? `（${msg.partialReason}）` : ''}</span>
                  </div>
                )}

                {msg.role === 'assistant' && (
                  <div className="absolute -bottom-5 left-0 flex items-center gap-1 text-[10px] text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity select-none font-mono">
                    <Coins size={10} />
                    {displayImages.some(img => img.isAi) ? (
                      <>
                        <span title="pay">💰¥{(msg.output_money ?? 0).toFixed(4)}</span>
                        {' / '}
                        <span title="total_money">🏦¥{(msg.total_money ?? 0).toFixed(4)}</span>
                      </>
                    ) : (
                      <>
                        <span title="input_token" className="inline-flex items-center gap-0.5"><ArrowUp size={10} className="text-green-500" />{msg.input_tokens ?? 0}</span>
                        {' / '}
                        <span title="output_token" className="inline-flex items-center gap-0.5"><ArrowDown size={10} className="text-orange-500" />{msg.output_tokens ?? 0}</span>
                        {' / '}
                        <span title="pay">💰¥{((msg.input_money ?? 0) + (msg.output_money ?? 0)).toFixed(4)}</span>
                        {' / '}
                        <span title="total_money">🏦¥{(msg.total_money ?? 0).toFixed(4)}</span>
                      </>
                    )}
                  </div>
                )}

                {msg.role === 'user' && (
                  <div className="absolute -bottom-5 left-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100">
                    <button 
                      onClick={() => handleCopyMessage(msg.content)}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                      title="复制消息"
                    >
                      <Copy size={14} />
                    </button>
                    <button 
                      onClick={() => handleDeleteUserMessage(Number(msg.id))}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="删除消息"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}

                {msg.role === 'assistant' && typeof msg.id === 'number' && (
                  <div className="absolute -bottom-5 right-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100">
                    <button 
                      onClick={() => onSendMessage('继续仅返回剩余代码即可（从上一次返回的最后一行代码第一个字符开始返回）')}
                      className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                      title="继续仅返回剩余代码即可"
                    >
                      ⬇️
                    </button>
                    <button 
                      onClick={() => handleResendMessage(Number(msg.id))}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                      title="重新发送"
                    >
                      <Send size={14} />
                    </button>
                    <button 
                      onClick={() => handleDeleteMessage(Number(msg.id))}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="删除对话"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {isAiThinking && (
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
              <Bot size={16} />
            </div>
            <div className="bg-[#18181b] border border-white/10 rounded-2xl rounded-tl-sm p-4 flex gap-1 items-center">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></span>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></span>
            </div>
          </div>
        )}

        {currentReasoning && (
          <ReasoningMarquee text={currentReasoning} variant="reasoning" />
        )}
        <div ref={bottomRef} />
      </div>

      {/* 3. Input Area */}
      <div className="p-4 bg-[#0c0c0e] border-t border-white/10 shrink-0">
        
        {/* Model Selector */}
        <div className="mb-3 flex items-center gap-3">
          <select 
            className="flex-1 bg-[#18181b] text-sm text-gray-300 border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-purple-500 cursor-pointer hover:bg-white/5"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value as any)}
          >
            <option value="gemini">Chat (文字聊天|图像理解)</option>
            <option value="nanobanana">Image (绘画|修图|分镜制作)</option>
          </select>
          
          <button 
            onClick={handleClearMessages}
            disabled={isAiThinking || messages.length === 0}
            className="px-4 py-2 bg-red-600/10 hover:bg-red-600/20 text-red-400 hover:text-red-300 border border-red-600/30 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="清空所有聊天记录"
          >
            清空消息
          </button>
        </div>

        {/* Upload Previews */}
        {selectedFiles.length > 0 && (
          <div className="flex gap-2 mb-2 overflow-x-auto pb-2 custom-scrollbar">
            {selectedFiles.map((url, idx) => (
              <div key={idx} className="relative group shrink-0">
                <img src={url} className="w-16 h-16 object-cover rounded-lg border border-white/20" />
                <button onClick={() => removeFile(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 shadow-md transition-opacity">
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl opacity-30 group-hover:opacity-70 transition duration-500 blur"></div>
          
          <div className="relative flex items-end bg-[#18181b] rounded-xl overflow-hidden p-1">
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUpload} />
            
            {/* Textarea for Multi-line Input */}
            <textarea 
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendCheck();
                }
              }}
              onPaste={handlePaste}
              disabled={isAiThinking}
              placeholder={selectedModel === 'nanobanana' ? "描述你想生成的图片内容..." : "输入需求，Shift+Enter换行..."}
              className="flex-1 bg-transparent text-white placeholder-gray-500 text-sm focus:outline-none py-3.5 px-2 resize-none max-h-32 custom-scrollbar"
              rows={1}
              style={{ minHeight: '96px' }}
            />
            
            {isAiThinking ? (
              <button
                onClick={onStopGeneration}
                className="p-3 mb-0.5 bg-red-600 hover:bg-red-500 text-white transition-colors m-1 rounded-lg shadow-lg"
                title="停止生成"
              >
                <Square size={18} />
              </button>
            ) : (
              <button
                onClick={handleSendCheck}
                disabled={(!input.trim() && selectedFiles.length === 0)}
                className="p-3 mb-0.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:bg-gray-700 disabled:text-gray-500 text-white transition-colors m-1 rounded-lg shadow-lg"
              >
                <Send size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Action Buttons - Upload and Cutout */}
        <div className="flex gap-2 mt-2">
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isAiThinking}
            className="flex-1 py-3 bg-[#18181b] hover:bg-white/5 text-gray-400 hover:text-white border border-white/10 rounded-lg transition-colors disabled:opacity-50"
            title="上传参考图片"
          >
            {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
            <span className="ml-2 text-sm">上传图片</span>
          </button>

          <button 
            onClick={handleCutout}
            disabled={selectedFiles.length === 0 || isAiThinking}
            className="flex-1 py-3 bg-[#18181b] hover:bg-white/5 text-purple-400 hover:text-purple-300 border border-purple-500/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="抠图"
          >
            <Scissors size={18} />
            <span className="ml-2 text-sm">抠图</span>
          </button>
        </div>
      </div>

      {/* 🔥 4. Confirm Modal */}
      {showConfirmModal && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#18181b] border border-white/10 rounded-xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-yellow-500/10 rounded-full">
                <ImageIcon className="text-yellow-500 w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">确认生成图片？</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  您当前选择了 <span className="text-yellow-400 font-medium">Nanobanana 生图模式</span>。
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  如果只是想修改代码或聊天，请切换回文本模型以节省 Token。
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-2.5">
              <button 
                onClick={() => executeSend('nanobanana')}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <ImageIcon size={16} />
                确认绘图
              </button>
              
              <button 
                onClick={() => executeSend('gemini')}
                className="w-full py-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-white/20 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2"
              >
                <MessageSquare size={16} />
                转为文本模型 (Gemini)
              </button>
              
              <button 
                onClick={() => setShowConfirmModal(false)}
                className="w-full py-2 text-xs text-gray-500 hover:text-gray-300 mt-1"
              >
                取消发送
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🚨 5. Clear Messages Confirm Modal */}
      {showClearConfirmModal && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#18181b] border border-red-500/50 rounded-xl p-6 w-full max-w-sm shadow-2xl shadow-red-500/20 animate-in fade-in zoom-in duration-200">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="text-6xl mb-4 animate-bounce">😱☠️🔥</div>
              <h3 className="text-2xl font-bold text-red-500 mb-2 animate-pulse">⚠️ 危险操作！⚠️</h3>
              <p className="text-lg text-red-400 font-bold mb-3">
                即将清空所有聊天记录！
              </p>
              <p className="text-sm text-gray-400">
                此操作<span className="text-red-500 font-bold">不可恢复</span>，所有对话历史将被永久删除！
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <button 
                onClick={executeClearMessages}
                className="w-full py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 border border-red-400"
              >
                <Trash2 size={18} />
                确认清空所有消息
              </button>
              
              <button 
                onClick={() => setShowClearConfirmModal(false)}
                className="w-full py-3 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg font-medium text-sm transition-all"
              >
                取消，我还要保留记录
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 评分确认模态框 */}
      {showEvaluateConfirmModal && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#18181b] border border-purple-500/30 rounded-xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-purple-500/10 rounded-full">
                <Award className="text-purple-400 w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">确认进行 AI 评分？</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  是否按<span className="text-purple-300 font-medium"> {lessonLabel} </span>的标准对当前代码进行打分？
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  评分将消耗 Token，请确认后继续。
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={executeEvaluate}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <Award size={16} />
                确认打分
              </button>
              <button
                onClick={() => setShowEvaluateConfirmModal(false)}
                className="w-full py-2 text-xs text-gray-500 hover:text-gray-300 mt-1"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 思维链确认模态框 */}
      {showMindmapConfirmModal && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-[#18181b] border border-cyan-500/30 rounded-xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-cyan-500/10 rounded-full">
                <Brain className="text-cyan-400 w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">确认生成思维链？</h3>
                {lessonLabel ? (
                  <p className="text-sm text-gray-400 leading-relaxed">
                    是否按<span className="text-cyan-300 font-medium"> {lessonLabel} </span>的标准生成当前代码的思维决策链？
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 leading-relaxed">
                    是否生成当前代码的思维决策链？
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  生成将消耗 Token，请确认后继续。
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <button
                onClick={executeMindmap}
                className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <Brain size={16} />
                确认生成
              </button>
              <button
                onClick={() => setShowMindmapConfirmModal(false)}
                className="w-full py-2 text-xs text-gray-500 hover:text-gray-300 mt-1"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token 超限警告模态框 */}
      <TokenWarningModal
        isOpen={showTokenWarning}
        onClose={() => setShowTokenWarning(false)}
        currentTokens={warningTokenCount}
      />

      {/* Vibe Audit 评分弹窗 */}
      <AuditModal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        result={auditResult}
        loading={isEvaluating}
      />

      {/* Vibe Audit 思维链弹窗 */}
      <MindmapModal
        isOpen={showMindmapModal}
        onClose={() => setShowMindmapModal(false)}
        result={mindmapResult}
        loading={isMindmapLoading}
      />

      {/* 学习目标弹窗（糖果色 2.5D 风格） */}
      <LearningObjectivesModal
        isOpen={showLearningObjectivesModal}
        onClose={() => setShowLearningObjectivesModal(false)}
        learningObjectives={currentLesson?.learning_objectives}
        lessonLabel={lessonLabel}
      />

    </div>
  );
};

export default ChatPanel;
